import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import RangeSlider from '../RangeSlider';
import Checkbox from '../../../Form/Checkbox';
import SegmentedControl from '../../../SegmentedControl';
import FilterDropdown from '../FilterDropdown';
import AdminService from '../../../../services/AdminService';
import { COUNTRY_OPTIONS } from '../../../../constants/main';
import classes from './styles.module.scss';

export default function FilterPanel({
  filters,
  onUpdateFilter,
  onUpdateFilters,
  onResetFilters,
  activeFiltersCount,
  activeSections,
  onToggleSection,
  // eslint-disable-next-line no-unused-vars
  sectionHasNonDefaultValues, // Kept for parent compatibility, not used in new checkbox logic
}) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    experienceRole: false,
    locationWork: false,
    availability: false,
    compensation: false,
    workAuth: false,
    languages: false,
    stability: false,
    jobsApplied: false,
  });
  // Store the expanded state before search was applied
  const previousExpandedStateRef = useRef(null);

  // Fetch and cache language options
  const { data: languagesData } = useQuery({
    queryKey: ['languages-all'],
    queryFn: () =>
      AdminService.getLanguages({
        orderBy: 'name',
        sortOrder: 'asc',
        term: '',
        pageNumber: 1,
        pageSize: 999,
      }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch and cache language level options
  const { data: languageLevelsData } = useQuery({
    queryKey: ['languageLevels-all'],
    queryFn: () =>
      AdminService.getLanguageLevels({
        orderBy: 'name',
        sortOrder: 'asc',
        term: '',
        pageNumber: 1,
        pageSize: 999,
      }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Convert fetched data to dropdown options format
  const languageOptions = useMemo(() => {
    if (!languagesData?.data) return [];
    return languagesData.data.map((lang) => ({
      label: lang.name,
      value: lang.id,
      originalData: lang,
    }));
  }, [languagesData]);

  const languageLevelOptions = useMemo(() => {
    if (!languageLevelsData?.data) return [];
    return languageLevelsData.data.map((level) => ({
      label: level.name,
      value: level.id,
      originalData: level,
    }));
  }, [languageLevelsData]);

  // Dropdown options (reused from CandidateForm)
  const currencyOptions = useMemo(
    () => [
      { label: 'USD', value: 'USD' },
      { label: 'EUR', value: 'EUR' },
      { label: 'INR', value: 'INR' },
    ],
    []
  );

  const frequencyOptions = useMemo(
    () => [
      {
        value: 'Hour',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Hour'
        ),
      },
      {
        value: 'Week',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Week'
        ),
      },
      {
        value: 'BiWeek',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.BiWeek'
        ),
      },
      {
        value: 'Month',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Month'
        ),
      },
      {
        value: 'Annual',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Annual'
        ),
      },
    ],
    [t]
  );

  const timezoneOptions = useMemo(
    () => [
      { label: 'EST', value: 'EST' },
      { label: 'PST', value: 'PST' },
      { label: 'CET', value: 'CET' },
      { label: 'APAC', value: 'APAC' },
    ],
    []
  );

  const travelPercentOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i <= 100; i += 5) {
      options.push({ label: `${i}%`, value: `${i}` });
    }
    return options;
  }, []);

  const seniorityOptions = useMemo(
    () => [
      { label: 'Junior', value: 1 },
      { label: 'Mid', value: 2 },
      { label: 'Senior', value: 3 },
      { label: 'Lead', value: 4 },
    ],
    []
  );

  // Memoized SegmentedControl items
  const compensationTypeItems = useMemo(
    () => [
      {
        value: 'Expected',
        label: t('intelSearch.filterPanel.expected'),
      },
      {
        value: 'Current',
        label: t('intelSearch.filterPanel.current'),
      },
    ],
    [t]
  );

  // Static "Any" option used to clear single-select dropdowns back to null
  const anyStaticOptions = useMemo(
    () => [
      {
        label: t('intelSearch.filterPanel.any', 'Any'),
        value: 'any',
        isStaticAny: true,
      },
    ],
    [t]
  );

  // Static options for Willingness to Upskill (Any / Yes / No)
  const willingnessToUpskillOptions = useMemo(
    () => [
      {
        label: t('intelSearch.filterPanel.any', 'Any'),
        value: 'any',
        isStaticAny: true,
      },
      { label: t('common.yes', 'Yes'), value: true },
      { label: t('common.no', 'No'), value: false },
    ],
    [t]
  );

  // Static options for Jobs Applied dropdown
  const jobAppliedStaticOptions = useMemo(
    () => [
      {
        label: t('intelSearch.filterPanel.jobsAppliedAny', 'Any'),
        value: 'any',
        isStaticAny: true,
      },
      {
        label: t('intelSearch.filterPanel.jobsAppliedNone', 'None'),
        value: 0,
      },
    ],
    [t]
  );

  // Helper for FilterDropdown compatibility
  const setFieldValue = useCallback(
    (name, value) => {
      // Extract the filter key from the name (e.g., 'filters.currency' -> 'currency')
      const key = name.split('.').pop();

      // Handle "Any" static option for Jobs Applied - reset to null (default)
      if (value?.isStaticAny) {
        onUpdateFilter(key, null);
        return;
      }

      onUpdateFilter(key, value);
    },
    [onUpdateFilter]
  );

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleClearAll = useCallback(() => {
    onResetFilters();
    setExpandedSections({
      experienceRole: false,
      locationWork: false,
      availability: false,
      compensation: false,
      workAuth: false,
      languages: false,
      stability: false,
      jobsApplied: false,
    });
  }, [onResetFilters]);

  const handleCheckboxChange = useCallback(
    (sectionKey) => {
      const isActive = activeSections?.[sectionKey] || false;
      const willBeActive = !isActive;

      // Toggle active state (applies/removes filters)
      onToggleSection(sectionKey);

      // Sync expanded state with checkbox state
      // When checking: expand section, when unchecking: collapse section
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: willBeActive,
      }));
    },
    [activeSections, onToggleSection]
  );

  const renderFilterHeader = useCallback(
    (title, sectionKey, showCheckbox = true) => {
      const isActive = activeSections?.[sectionKey] || false;

      return (
        <div className={classes.filterHeader}>
          {showCheckbox && (
            <div onClick={(e) => e.stopPropagation()} role="presentation">
              <Checkbox
                isSelected={isActive}
                onChange={() => handleCheckboxChange(sectionKey)}
              />
            </div>
          )}
          <span
            className={classes.filterTitle}
            onClick={() => toggleSection(sectionKey)}
          >
            {title}
          </span>
          <div
            className={classNames(classes.arrow, {
              [classes.collapsed]: !expandedSections[sectionKey],
            })}
            onClick={() => toggleSection(sectionKey)}
          />
        </div>
      );
    },
    [activeSections, handleCheckboxChange, toggleSection, expandedSections]
  );

  // Filter sections based on search term (section titles and field labels)
  const filteredSections = useMemo(() => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      // No search term - show all sections
      return {
        experienceRole: true,
        locationWork: true,
        availability: true,
        compensation: true,
        workAuth: true,
        languages: true,
        stability: true,
        jobsApplied: true,
      };
    }

    const searchLower = searchTerm.toLowerCase().trim();

    // Helper function to check if any string matches
    const matches = (text) => text.toLowerCase().includes(searchLower);

    // Helper function to check section title or any field labels
    const checkSection = (sectionTitle, fieldLabels) => {
      if (matches(t(sectionTitle))) return true;
      return fieldLabels.some((labelKey) => matches(t(labelKey)));
    };

    const sections = {};

    // Experience & Role
    sections.experienceRole = checkSection(
      'intelSearch.filterPanel.sections.experienceRole',
      [
        'intelSearch.filterPanel.yearsExperience',
        'intelSearch.filterPanel.seniority',
      ]
    );

    // Location & Work Mode
    sections.locationWork = checkSection(
      'intelSearch.filterPanel.sections.locationWork',
      [
        'intelSearch.filterPanel.country',
        'intelSearch.filterPanel.city',
        'intelSearch.filterPanel.remoteHybridOnsite',
        'intelSearch.filterPanel.willingToRelocate',
        'intelSearch.filterPanel.timeZoneOverlap',
        'intelSearch.filterPanel.travelAvailability',
      ]
    );

    // Availability
    sections.availability = checkSection(
      'intelSearch.filterPanel.sections.availability',
      [
        'intelSearch.filterPanel.immediateJoiner',
        'intelSearch.filterPanel.noticePeriod',
        'intelSearch.filterPanel.shiftReadiness',
      ]
    );

    // Compensation
    sections.compensation = checkSection(
      'intelSearch.filterPanel.sections.compensation',
      [
        'intelSearch.filterPanel.currency',
        'intelSearch.filterPanel.frequency',
        'intelSearch.filterPanel.salaryIncrease',
        'intelSearch.filterPanel.expected',
        'intelSearch.filterPanel.current',
      ]
    );

    // Work Authorization
    sections.workAuth = checkSection(
      'intelSearch.filterPanel.sections.workAuth',
      ['intelSearch.filterPanel.visaWorkPermit']
    );

    // Languages & Communication
    sections.languages = checkSection(
      'intelSearch.filterPanel.sections.languages',
      [
        'intelSearch.filterPanel.language',
        'intelSearch.filterPanel.proficiencyLevel',
      ]
    );

    // Stability & Engagement
    sections.stability = checkSection(
      'intelSearch.filterPanel.sections.stability',
      [
        'intelSearch.filterPanel.employmentStability',
        'intelSearch.filterPanel.engagementPreference',
        'intelSearch.filterPanel.willingnessToUpskill',
      ]
    );

    // Jobs Applied
    sections.jobsApplied = checkSection(
      'intelSearch.filterPanel.sections.jobsApplied',
      ['intelSearch.filterPanel.jobsApplied']
    );

    return sections;
  }, [searchTerm, t]);

  // Sync expanded state with active sections (when sections are auto-checked via filter changes)
  // Only expand when section becomes active, don't collapse when inactive (user might have manually expanded)
  useEffect(() => {
    setExpandedSections((prev) => {
      const updated = { ...prev };
      let hasChanges = false;

      Object.keys(activeSections || {}).forEach((sectionKey) => {
        // If section becomes active and is not expanded, expand it
        if (activeSections[sectionKey] && !prev[sectionKey]) {
          updated[sectionKey] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [activeSections]);

  // Auto-expand sections that match the search term
  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      // If search is cleared, restore previous expanded state
      if (previousExpandedStateRef.current !== null) {
        setExpandedSections(previousExpandedStateRef.current);
        previousExpandedStateRef.current = null;
      }
      return;
    }

    // Expand all sections that match the search
    setExpandedSections((prev) => {
      // Save current expanded state before applying search (only on first search)
      if (previousExpandedStateRef.current === null) {
        previousExpandedStateRef.current = { ...prev };
      }

      const updated = { ...prev };
      Object.keys(filteredSections).forEach((sectionKey) => {
        if (filteredSections[sectionKey]) {
          updated[sectionKey] = true;
        }
      });
      return updated;
    });
  }, [searchTerm, filteredSections]);

  return (
    <div className={classes.FilterPanel}>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <h3 className={classes.title}>
            {t('intelSearch.filterPanel.title')}
          </h3>
          {activeFiltersCount > 0 && (
            <div className={classes.filterCount}>{activeFiltersCount}</div>
          )}
        </div>
        <button
          type="button"
          className={classes.clearButton}
          onClick={handleClearAll}
        >
          {t('intelSearch.filterPanel.clear')}
        </button>
      </div>

      <div className={classes.searchBox}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="7"
            cy="7"
            r="5"
            stroke="#C1C5D6"
            strokeWidth="1.5"
            fill="none"
          />
          <path
            d="M11 11L14 14"
            stroke="#C1C5D6"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="text"
          placeholder={t('intelSearch.filterPanel.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type="button"
            className={classes.clearSearchButton}
            onClick={() => setSearchTerm('')}
            title={t('intelSearch.filterPanel.clearSearch')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="#9CA3AF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className={classes.divider} />

      <div className={classes.filtersContainer}>
        {/* Experience & Role */}
        {filteredSections.experienceRole && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.experienceRole'),
              'experienceRole'
            )}
            {expandedSections.experienceRole && (
              <div className={classes.filterContent}>
                <div className={classes.rangeField}>
                  <div className={classes.rangeHeader}>
                    <label
                      htmlFor="years-experience"
                      className={classes.rangeLabel}
                    >
                      {t('intelSearch.filterPanel.yearsExperience')}
                    </label>
                    <span className={classes.rangeValue}>
                      {filters.yearsExperience[1]} years
                    </span>
                  </div>
                  <RangeSlider
                    min={0}
                    max={30}
                    step={1}
                    value={filters.yearsExperience}
                    onChange={(value) =>
                      onUpdateFilter('yearsExperience', value)
                    }
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.seniority"
                    label={t('intelSearch.filterPanel.seniority')}
                    value={filters.seniority}
                    setFieldValue={setFieldValue}
                    options={seniorityOptions}
                    placeholder={t('intelSearch.filterPanel.selectSeniority')}
                  />
                </div>
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Location & Work Mode */}
        {filteredSections.locationWork && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.locationWork'),
              'locationWork'
            )}
            {expandedSections.locationWork && (
              <div className={classes.filterContent}>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.country"
                    label={t('intelSearch.filterPanel.country')}
                    value={filters.country}
                    setFieldValue={setFieldValue}
                    placeholder={t('intelSearch.filterPanel.selectCountries')}
                    options={COUNTRY_OPTIONS}
                    multiSelect
                  />
                </div>
                <div className={classes.field}>
                  <label htmlFor="filters.city" className={classes.fieldLabel}>
                    {t('intelSearch.filterPanel.city')}
                  </label>
                  <input
                    type="text"
                    id="filters.city"
                    className={classes.textInput}
                    placeholder={t('intelSearch.filterPanel.enterCity')}
                    value={filters.city}
                    onChange={(e) => onUpdateFilter('city', e.target.value)}
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.workMode"
                    label={t('intelSearch.filterPanel.remoteHybridOnsite')}
                    value={filters.workMode}
                    setFieldValue={setFieldValue}
                    placeholder={t('intelSearch.filterPanel.selectWorkMode')}
                    multiSelect
                    fetchOptions={{
                      dataName: 'workModeOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getJobLocationTypes({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                  />
                </div>
                <div className={classes.checkboxField}>
                  <span className={classes.checkboxLabel}>
                    {t('intelSearch.filterPanel.willingToRelocate')}
                  </span>
                  <Checkbox
                    isSelected={filters.willingToRelocate}
                    onChange={() =>
                      onUpdateFilter(
                        'willingToRelocate',
                        !filters.willingToRelocate
                      )
                    }
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.timezoneOverlap"
                    label={t('intelSearch.filterPanel.timeZoneOverlap')}
                    value={filters.timezoneOverlap}
                    setFieldValue={setFieldValue}
                    options={timezoneOptions}
                    placeholder={t('intelSearch.filterPanel.selectTimezone')}
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.travelPercentage"
                    label={t('intelSearch.filterPanel.travelAvailability')}
                    value={filters.travelPercentage}
                    setFieldValue={setFieldValue}
                    options={travelPercentOptions}
                    placeholder={t(
                      'intelSearch.filterPanel.travelPercentageRange'
                    )}
                  />
                </div>
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Availability */}
        {filteredSections.availability && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.availability'),
              'availability'
            )}
            {expandedSections.availability && (
              <div className={classes.filterContent}>
                <div className={classes.checkboxField}>
                  <span className={classes.checkboxLabel}>
                    {t('intelSearch.filterPanel.immediateJoiner')}
                  </span>
                  <Checkbox
                    isSelected={filters.immediateJoiner}
                    readonly={!!filters.noticePeriod}
                    onChange={() => {
                      const newValue = !filters.immediateJoiner;
                      if (newValue) {
                        onUpdateFilters({
                          immediateJoiner: true,
                          noticePeriod: null,
                        });
                      } else {
                        onUpdateFilter('immediateJoiner', false);
                      }
                    }}
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.noticePeriod"
                    label={t('intelSearch.filterPanel.noticePeriod')}
                    value={filters.noticePeriod}
                    setFieldValue={(name, value) => {
                      if (value && value.label?.trim() === '0') {
                        onUpdateFilters({
                          noticePeriod: null,
                          immediateJoiner: true,
                        });
                      } else {
                        onUpdateFilters({
                          noticePeriod: value,
                          immediateJoiner: false,
                        });
                      }
                    }}
                    readonly={filters.immediateJoiner}
                    placeholder={t(
                      'intelSearch.filterPanel.selectNoticePeriod'
                    )}
                    fetchOptions={{
                      dataName: 'noticePeriodOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getNoticePeriods({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.shiftReadiness"
                    label={t('intelSearch.filterPanel.shiftReadiness')}
                    value={filters.shiftReadiness}
                    setFieldValue={setFieldValue}
                    placeholder={t(
                      'intelSearch.filterPanel.selectShiftReadiness'
                    )}
                    fetchOptions={{
                      dataName: 'shiftReadinessOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getWorkShiftReadinesses({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                  />
                </div>
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Compensation */}
        {filteredSections.compensation && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.compensation'),
              'compensation'
            )}
            {expandedSections.compensation && (
              <div className={classes.filterContent}>
                <div className={classes.compensationTabs}>
                  <SegmentedControl
                    items={compensationTypeItems}
                    selectedItem={{
                      value: filters.compensationType,
                      label: filters.compensationType,
                    }}
                    onChange={(item) =>
                      onUpdateFilter('compensationType', item.value)
                    }
                  />
                </div>
                <div className={classes.twoColumn}>
                  <FilterDropdown
                    name="filters.currency"
                    value={filters.currency}
                    setFieldValue={setFieldValue}
                    options={currencyOptions}
                    placeholder={t('intelSearch.filterPanel.currency')}
                  />
                  <FilterDropdown
                    name="filters.frequency"
                    value={filters.frequency}
                    setFieldValue={setFieldValue}
                    options={frequencyOptions}
                    placeholder={t('intelSearch.filterPanel.frequency')}
                  />
                </div>
                <RangeSlider
                  min={0}
                  max={1000000}
                  step={1000}
                  value={filters.salaryRange}
                  onChange={(value) => onUpdateFilter('salaryRange', value)}
                  formatValue={(val) => val.toLocaleString()}
                />
                <div className={classes.field}>
                  <label
                    htmlFor="salary-increase"
                    className={classes.fieldLabel}
                  >
                    {t('intelSearch.filterPanel.salaryIncrease')}
                  </label>
                </div>
                <RangeSlider
                  min={0}
                  max={100}
                  step={1}
                  value={filters.salaryIncrease}
                  onChange={(value) => onUpdateFilter('salaryIncrease', value)}
                  formatValue={(val) => `${val}%`}
                />
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Work Authorization */}
        {filteredSections.workAuth && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.workAuth'),
              'workAuth'
            )}
            {expandedSections.workAuth && (
              <div className={classes.filterContent}>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.visaWorkPermit"
                    label={t('intelSearch.filterPanel.visaWorkPermit')}
                    value={filters.visaWorkPermit}
                    setFieldValue={setFieldValue}
                    placeholder={t(
                      'intelSearch.filterPanel.selectVisaWorkPermits'
                    )}
                    fetchOptions={{
                      dataName: 'workAuthStatusesOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getWorkAuthStatuses({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    multiSelect
                  />
                </div>
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Languages & Communication */}
        {filteredSections.languages && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.languages'),
              'languages'
            )}
            {expandedSections.languages && (
              <div className={classes.filterContent}>
                {/* Language + Level Entries */}
                {filters.languages && filters.languages.length > 0 && (
                  <div className={classes.languagesList}>
                    {filters.languages.map((langEntry, index) => {
                      // Use the unique ID for keys
                      const entryId = langEntry.id || `entry-${index}`;

                      return (
                        <div key={entryId} className={classes.languageEntry}>
                          <div className={classes.languageRow}>
                            <FilterDropdown
                              name={`filters.languages.${index}.language`}
                              label={t('intelSearch.filterPanel.language')}
                              value={langEntry.language}
                              setFieldValue={(_, value) => {
                                const updated = [...filters.languages];
                                updated[index] = {
                                  ...updated[index],
                                  language: value,
                                };
                                onUpdateFilter('languages', updated);
                              }}
                              placeholder={t(
                                'intelSearch.filterPanel.selectLanguage'
                              )}
                              options={languageOptions}
                            />
                            <FilterDropdown
                              name={`filters.languages.${index}.level`}
                              label={t(
                                'intelSearch.filterPanel.proficiencyLevel'
                              )}
                              value={langEntry.level}
                              setFieldValue={(_, value) => {
                                const updated = [...filters.languages];
                                updated[index] = {
                                  ...updated[index],
                                  level: value,
                                };
                                onUpdateFilter('languages', updated);
                              }}
                              placeholder={t(
                                'intelSearch.filterPanel.selectLevel'
                              )}
                              options={languageLevelOptions}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = filters.languages.filter(
                                  (_, i) => i !== index
                                );
                                onUpdateFilter('languages', updated);
                              }}
                              className={classes.removeButton}
                              title={t('common.remove')}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Language Button */}
                <button
                  type="button"
                  onClick={() => {
                    const updated = [
                      ...(filters.languages || []),
                      {
                        id: `temp-${Date.now()}-${Math.random()}`,
                        language: null,
                        level: null,
                      },
                    ];
                    onUpdateFilter('languages', updated);
                  }}
                  className={classes.addButton}
                >
                  + {t('intelSearch.filterPanel.addLanguage')}
                </button>

                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Stability & Engagement */}
        {filteredSections.stability && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.stability'),
              'stability'
            )}
            {expandedSections.stability && (
              <div className={classes.filterContent}>
                {/* TODO: Hidden until backend field is implemented
                <div className={classes.switchField}>
                  <span className={classes.fieldLabel}>
                    {t('intelSearch.filterPanel.employmentStability')}
                  </span>
                  <GradientSwitch
                    isEnabled={filters.employmentStability}
                    onClick={() =>
                      onUpdateFilter(
                        'employmentStability',
                        !filters.employmentStability
                      )
                    }
                  />
                </div>
                */}
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.engagementPreference"
                    label={t('intelSearch.filterPanel.engagementPreference')}
                    value={filters.engagementPreference}
                    setFieldValue={setFieldValue}
                    placeholder={t('intelSearch.filterPanel.any', 'Any')}
                    staticPrependOptions={anyStaticOptions}
                    fetchOptions={{
                      dataName: 'engagementPreferenceOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getEngagementPreferences({
                          orderBy: 'name',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                  />
                </div>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.willingnessToUpskill"
                    label={t('intelSearch.filterPanel.willingnessToUpskill')}
                    value={willingnessToUpskillOptions.find(
                      (opt) => opt.value === filters.willingnessToUpskill
                    )}
                    setFieldValue={(_name, option) => {
                      if (!option || option.isStaticAny) {
                        onUpdateFilter('willingnessToUpskill', null);
                      } else {
                        onUpdateFilter('willingnessToUpskill', option.value);
                      }
                    }}
                    options={willingnessToUpskillOptions}
                    placeholder={t('intelSearch.filterPanel.any', 'Any')}
                  />
                </div>
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}

        {/* Jobs Applied */}
        {filteredSections.jobsApplied && (
          <div className={classes.filterSection}>
            {renderFilterHeader(
              t('intelSearch.filterPanel.sections.jobsApplied', 'Jobs Applied'),
              'jobsApplied'
            )}
            {expandedSections.jobsApplied && (
              <div className={classes.filterContent}>
                <div className={classes.field}>
                  <FilterDropdown
                    name="filters.jobAppliedFilter"
                    value={filters.jobAppliedFilter}
                    setFieldValue={setFieldValue}
                    placeholder={t(
                      'intelSearch.filterPanel.jobsAppliedAny',
                      'Any'
                    )}
                    staticPrependOptions={jobAppliedStaticOptions}
                    fetchOptions={{
                      dataName: 'jobPostsForAppliedFilter',
                      isJobPostsPicker: true,
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getJobPosts({
                          orderBy: 'jobTitle',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                          pageSize: 100,
                        }),
                    }}
                  />
                </div>
                <div className={classes.verticalLine} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
